/**
 * LossesAvoidedHero — gamified count-up banner for the Pallet dashboard.
 *
 * Renders a large "A$X,XXX saved YTD · N CHEP replacements you didn't
 * pay for" line above the KPI tiles. The dollar figure animates from 0
 * to the target in ~1.2s on mount so the number lands with a small dopamine
 * hit — the exact reason the user asked for the widget.
 *
 * When the tenant has zero recovered pallets YTD we swap in a soft
 * "You haven't recovered any pallets yet this year — enable magic-link
 * returns to start clawing back losses" prompt instead of A$0.
 */
import React, { useEffect, useState, useRef } from 'react';
import { CurrencyCircleDollar, Sparkle, LinkedinLogo, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Ease-out cubic — starts fast, decelerates so the number visually
// "lands" on the final value.
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

function useCountUp(target, durationMs = 1200) {
  const [value, setValue] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    if (target == null) return undefined;
    cancelAnimationFrame(raf.current);
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / durationMs);
      setValue(target * easeOutCubic(t));
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, durationMs]);
  return value;
}

export default function LossesAvoidedHero({ data }) {
  const target = data?.value_aud ?? 0;
  const animated = useCountUp(target);
  const count = data?.count_pallets ?? 0;
  const top = data?.top_type;
  const [shareCopied, setShareCopied] = useState(false);

  // Zero-state — encourage magic-link adoption without preaching.
  if (target === 0 && count === 0) {
    return (
      <div
        data-testid="losses-avoided-hero"
        className="relative overflow-hidden border border-emerald-200 bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50 p-5 md:p-6"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/60 border border-emerald-200 rounded">
            <Sparkle size={20} weight="fill" className="text-emerald-600" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Losses avoided this year</div>
            <div className="text-lg md:text-xl font-bold text-emerald-900 mt-0.5" data-testid="losses-avoided-zero-state">
              No pallets clawed back yet — turn on magic-link returns to start.
            </div>
            <div className="text-xs text-emerald-800/80 mt-1">
              Merchants receive a one-click &quot;did you return this?&quot; link in every recall email. Every yes adds to the counter.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const topLine = top
    ? `${count.toLocaleString()} pallets · ${top.count.toLocaleString()} ${top.code} replacement${top.count === 1 ? '' : 's'} you didn't pay for`
    : `${count.toLocaleString()} pallet${count === 1 ? '' : 's'} clawed back from the pooler this year`;

  const animatedInt = Math.round(animated);

  // Compose a share-ready one-liner. LinkedIn's public share endpoint only
  // accepts a URL (they removed `summary` years ago), so the play is:
  // (1) copy the pre-composed brag to clipboard, (2) open LinkedIn's compose
  // page in a new tab, (3) toast the user to paste. The stockaris.com.au
  // landing page's OpenGraph card also renders on LinkedIn's preview.
  const targetYear = new Date().getFullYear();
  const shareText = top
    ? `Recovered A$${Math.round(target).toLocaleString()} in ${top.code} pallets with @Stockaris in ${targetYear} — ${top.count.toLocaleString()} replacements we didn't pay for. #WarehouseOps #Logistics`
    : `Recovered A$${Math.round(target).toLocaleString()} in pooled pallets with @Stockaris in ${targetYear} — ${count.toLocaleString()} clawed back from the pooler. #WarehouseOps`;

  const handleShare = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareText);
      }
    } catch (_) { /* clipboard may be blocked in insecure contexts — proceed anyway */ }
    setShareCopied(true);
    toast.success('Copied — paste into LinkedIn to share.', { duration: 3500 });
    setTimeout(() => setShareCopied(false), 2500);
    const url = 'https://www.linkedin.com/sharing/share-offsite/?url=' + encodeURIComponent('https://stockaris.com.au');
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  return (
    <div
      data-testid="losses-avoided-hero"
      className="relative overflow-hidden border border-emerald-300 bg-gradient-to-br from-emerald-50 via-emerald-100 to-teal-100 p-5 md:p-7"
    >
      {/* Subtle diagonal shimmer overlay — pure CSS, no motion library needed */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          background:
            'repeating-linear-gradient(45deg, transparent 0 12px, #059669 12px 13px)',
        }}
      />
      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-emerald-700">
            <CurrencyCircleDollar size={12} weight="fill" />
            Losses avoided · YTD {new Date().getFullYear()}
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span
              className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight font-mono text-emerald-900"
              data-testid="losses-avoided-value"
            >
              A${animatedInt.toLocaleString()}
            </span>
            <span className="text-xs text-emerald-800/70 font-mono">saved</span>
          </div>
          <div className="mt-1 text-sm md:text-base text-emerald-900/90" data-testid="losses-avoided-subline">
            {topLine}
          </div>
        </div>
        <div className="hidden md:flex md:flex-col md:items-end md:gap-3">
          <Sparkle size={56} weight="fill" className="text-emerald-500 opacity-70" />
          <button
            type="button"
            onClick={handleShare}
            data-testid="losses-avoided-share-linkedin"
            title="Copy the number and open LinkedIn to share"
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-widest border border-emerald-300 bg-white/70 text-emerald-800 hover:bg-white hover:border-emerald-500 hover:text-emerald-900 rounded-md shadow-sm transition-colors"
          >
            {shareCopied ? (
              <Check size={14} weight="bold" />
            ) : (
              <LinkedinLogo size={14} weight="fill" />
            )}
            <span>{shareCopied ? 'Copied · switch to LinkedIn' : 'Share to LinkedIn'}</span>
          </button>
        </div>
      </div>
      {/* Mobile share button below the copy (md:hidden) so it stays reachable */}
      <div className="relative mt-3 md:hidden">
        <button
          type="button"
          onClick={handleShare}
          data-testid="losses-avoided-share-linkedin-mobile"
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-widest border border-emerald-300 bg-white/70 text-emerald-800 hover:bg-white rounded-md shadow-sm"
        >
          {shareCopied ? (
            <Check size={14} weight="bold" />
          ) : (
            <LinkedinLogo size={14} weight="fill" />
          )}
          <span>{shareCopied ? 'Copied · open LinkedIn' : 'Share to LinkedIn'}</span>
        </button>
      </div>
    </div>
  );
}
